'use client'

import React, { useState, useEffect, useRef } from 'react'
import { Container, Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { Animations, MultiReveal, Reveal } from '@quark/animation'
import isMobile from 'ismobilejs'
import { createScrollHandler } from '@utils/mobileScrollUtils'
import BusinessModals from '@components/BusinessModal/businessModals'
import PrimaryCard from './PrimaryCard'
import SecondaryCard from './SecondaryCard'
import styles from './PrimaryCard/primaryCard.module.scss'
import coveragesStyles from './coverages.module.scss'

const Coverages = (data: any) => {
  const isMobileDevice = isMobile()?.phone
  const [activeDotIndex, setActiveDotIndex] = useState<number>(0)
  const [formSnippet, setFormSnippet] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const mobileScrollRef = useRef<HTMLDivElement | null>(null)
  const primaryCoverages = data?.coverageList?.filter((coverage: any) => coverage?.cardType === 'primary') || []

  useEffect(() => {
    if (!isMobileDevice || !primaryCoverages?.length || !mobileScrollRef.current) return

    const handleScroll = createScrollHandler(mobileScrollRef, cardRefs, setActiveDotIndex)

    mobileScrollRef.current.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      if (mobileScrollRef.current) {
        mobileScrollRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [primaryCoverages, isMobileDevice])

  return (
    <Container
      type="fluid"
      backgroundColor="base/invert"
      paddingTop={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-48' }}
      paddingBottom={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-48' }}
      position="relative"
      overflow="hidden">
      <Container type="root">
        <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
          <Container type="max-parent" paddingBottom="spacing-48">
            <Text
              as="h1"
              fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-3xl/bold' }}
              color="static/Primary-onDark"
              textAlign={{ lg: 'center', md: 'center', sm: 'center' }}>
              {data?.title}
            </Text>
          </Container>
        </Reveal>
        <Container zIndex="content" position="relative">
          {isMobileDevice ? (
            <>
              <div ref={mobileScrollRef} className={coveragesStyles.mobileScrollContainer}>
                {primaryCoverages.map((coverage: any, index: number) => (
                  <div
                    key={coverage?.id}
                    ref={el => {
                      cardRefs.current[index] = el
                    }}
                    className={coveragesStyles.coverageCardContainer}>
                    <PrimaryCard
                      data={coverage}
                      variant={index + 1}
                      setIsModalOpen={setIsModalOpen}
                      setFormSnippet={setFormSnippet}
                    />
                  </div>
                ))}
              </div>
              {primaryCoverages && primaryCoverages.length > 1 && (
                <div className={coveragesStyles.dotsContainer}>
                  {primaryCoverages.map((_: any, index: number) => (
                    <div
                      key={index}
                      className={`${coveragesStyles.dot} ${activeDotIndex === index ? coveragesStyles.activeDot : ''}`}
                    />
                  ))}
                </div>
              )}
              {data?.coverageList
                ?.filter((coverage: any) => coverage?.cardType === 'secondary')
                ?.map((coverage: any) => (
                  <SecondaryCard
                    key={coverage?.id}
                    data={coverage}
                    setIsModalOpen={setIsModalOpen}
                    setFormSnippet={setFormSnippet}
                  />
                ))}
            </>
          ) : (
            <MultiReveal
              as={Flex}
              gap={{
                lg: 'spacing-20',
                md: 'spacing-16',
                sm: 'spacing-16',
              }}
              animationDelays={(i: number) => 300 + i * 100}
              animationProps={{ animation: Animations.FadeIn50px }}
              flexWrap="nowrap"
              justifyContent="space-evenly">
              {data?.coverageList?.map((coverage: any, index: number) =>
                coverage?.cardType === 'primary' ? (
                  <PrimaryCard
                    key={coverage?.id}
                    data={coverage}
                    variant={index + 1}
                    setIsModalOpen={setIsModalOpen}
                    setFormSnippet={setFormSnippet}
                  />
                ) : (
                  <SecondaryCard
                    key={coverage?.id}
                    data={coverage}
                    setIsModalOpen={setIsModalOpen}
                    setFormSnippet={setFormSnippet}
                  />
                )
              )}
            </MultiReveal>
          )}
        </Container>
      </Container>
      <div className={styles.coveragesContainer} />
      <BusinessModals isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} content={formSnippet} />
    </Container>
  )
}

export default Coverages
